/**
 * Normalize HR API rows for pages that still read snake_case.
 * Express routes use convert*ToFrontend (camelCase); these helpers mirror both shapes.
 */

const num01 = (v) => (v === true || v === 1 || v === '1') ? 1 : 0;

/** Accident workflow statuses — English API values; Arabic labels via i18n. */
export const ACCIDENT_STATUS_VALUES = {
  declared: 'declared',
  investigation: 'investigation',
  closed: 'closed',
  dispute: 'dispute',
};

export const LEGACY_ACCIDENT_STATUS_MAP = {
  déclaré: ACCIDENT_STATUS_VALUES.declared,
  enquête: ACCIDENT_STATUS_VALUES.investigation,
  clos: ACCIDENT_STATUS_VALUES.closed,
  contentieux: ACCIDENT_STATUS_VALUES.dispute,
};

export function normalizeAccidentStatus(status) {
  if (!status) return ACCIDENT_STATUS_VALUES.declared;
  return LEGACY_ACCIDENT_STATUS_MAP[status] || status;
}

/** @param {object} d */
export function normalizeDepartureRecord(d) {
  if (!d || typeof d !== 'object') return d;
  return {
    ...d,
    departure_number: d.departureNumber ?? d.departure_number ?? '',
    employee_id: d.employeeId ?? d.employee_id ?? '',
    employee_name: d.employeeName ?? d.employee_name ?? '',
    departure_date: d.departureDate ?? d.departure_date ?? '',
    notice_date: d.noticeDate ?? d.notice_date ?? '',
    departure_type: d.departureType ?? d.departure_type ?? '',
    departure_reason: d.departureReason ?? d.departure_reason ?? '',
    status: d.status ?? '',
    final_settlement_amount: d.finalSettlementAmount ?? d.final_settlement_amount ?? 0,
    settlement_date: d.settlementDate ?? d.settlement_date ?? '',
    exit_interview_conducted: num01(d.exitInterviewConducted ?? d.exit_interview_conducted),
    exit_interview_notes: d.exitInterviewNotes ?? d.exit_interview_notes ?? '',
    rehire_eligible: num01(d.rehireEligible ?? d.rehire_eligible),
    notes: d.notes ?? '',
  };
}

/** @param {object} s */
export function normalizeSanctionRecord(s) {
  if (!s || typeof s !== 'object') return s;
  return {
    ...s,
    sanction_number: s.sanctionNumber ?? s.sanction_number ?? '',
    employee_id: s.employeeId ?? s.employee_id ?? '',
    employee_name: s.employeeName ?? s.employee_name ?? '',
    incident_date: s.incidentDate ?? s.incident_date ?? '',
    sanction_date: s.sanctionDate ?? s.sanction_date ?? '',
    incident_type: s.incidentType ?? s.incident_type ?? '',
    severity: s.severity ?? '',
    sanction_description: s.sanctionDescription ?? s.sanction_description ?? '',
    witness_names: s.witnessNames ?? s.witness_names ?? '',
    status: s.status ?? '',
    appeal_date: s.appealDate ?? s.appeal_date ?? '',
    appeal_result: s.appealResult ?? s.appeal_result ?? '',
    suspension_days: s.suspensionDays ?? s.suspension_days ?? 0,
    suspension_start: s.suspensionStart ?? s.suspension_start ?? '',
    suspension_end: s.suspensionEnd ?? s.suspension_end ?? '',
    penalty_amount: s.penaltyAmount ?? s.penalty_amount ?? 0,
    notes: s.notes ?? '',
  };
}

/** @param {object} a */
export function normalizeAccidentRecord(a) {
  if (!a || typeof a !== 'object') return a;
  return {
    ...a,
    accident_number: a.accidentNumber ?? a.accident_number ?? '',
    employee_id: a.employeeId ?? a.employee_id ?? '',
    employee_name: a.employeeName ?? a.employee_name ?? '',
    accident_date: a.accidentDate ?? a.accident_date ?? '',
    accident_time: a.accidentTime ?? a.accident_time ?? '',
    report_date: a.reportDate ?? a.report_date ?? '',
    location: a.location ?? '',
    accident_type: a.accidentType ?? a.accident_type ?? '',
    severity: a.severity ?? '',
    injury_description: a.injuryDescription ?? a.injury_description ?? '',
    body_part_affected: a.bodyPartAffected ?? a.body_part_affected ?? '',
    witness_names: a.witnessNames ?? a.witness_names ?? '',
    first_aid_provided: a.firstAidProvided ?? a.first_aid_provided ?? '',
    status: normalizeAccidentStatus(a.status ?? ''),
    medical_leave_days: a.medicalLeaveDays ?? a.medical_leave_days ?? 0,
    leave_start: a.leaveStart ?? a.leave_start ?? '',
    leave_end: a.leaveEnd ?? a.leave_end ?? '',
    compensation_amount: a.compensationAmount ?? a.compensation_amount ?? 0,
    insurance_claim_number: a.insuranceClaimNumber ?? a.insurance_claim_number ?? '',
    cnas_notified: num01(a.cnasNotified ?? a.cnas_notified ?? a.gosiNotified ?? a.gosi_notified),
    cnas_notification_date: a.cnasNotificationDate ?? a.cnas_notification_date ?? a.gosiNotificationDate ?? a.gosi_notification_date ?? '',
    gosi_notified: num01(a.gosiNotified ?? a.gosi_notified ?? a.cnasNotified ?? a.cnas_notified),
    gosi_notification_date: a.gosiNotificationDate ?? a.gosi_notification_date ?? a.cnasNotificationDate ?? a.cnas_notification_date ?? '',
    investigation_notes: a.investigationNotes ?? a.investigation_notes ?? '',
    corrective_actions: a.correctiveActions ?? a.corrective_actions ?? '',
    notes: a.notes ?? '',
  };
}

